import type { WorkbenchPreviewCrash } from '../stores/workbench';

/**
 * `window` event the workbench dispatches when the user clicks "Fix with AI" on a crashed
 * preview; `detail` is the `WorkbenchPreviewCrash`. The conversation module listens and sends
 * `buildPreviewCrashFixPrompt(detail)` as a normal prototyper turn.
 */
export const WORKBENCH_FIX_PREVIEW_CRASH_EVENT = 'ideoz-workbench-fix-preview-crash';

const MAX_STACK_LINES = 8;
const MAX_MESSAGE_CHARS = 1_500;

// eslint-disable-next-line no-control-regex
const ANSI_ESCAPES = /\[[0-9;]*m/g;

function clean(text: string): string {
  return text.replace(ANSI_ESCAPES, '').trim();
}

/** `/home/project/src/App.tsx:12:3` → `src/App.tsx:12:3`, so the path matches the workspace. */
function workspacePath(file: string): string {
  return clean(file).replace(/^.*?\/(src\/)/, '$1');
}

function stackHead(stack: string | undefined, message: string): string {
  if (!stack) return '';
  const lines = clean(stack)
    .split('\n')
    .map((line) => line.trimEnd())
    .filter(Boolean);
  // Browsers start the stack with "Error: <message>", which the prompt already shows.
  if (lines[0] && message && lines[0].includes(message.split('\n')[0])) lines.shift();
  return lines.slice(0, MAX_STACK_LINES).join('\n');
}

/**
 * The screen a crash happened on, or null when it cannot be told. The preview iframe reports
 * `window.location.pathname`, which in a light-preview blob document is the blob's own address
 * (`http://localhost:3000/<uuid>`), and generated apps route with a hash (`HashRouter`), so the
 * real screen is not in it at all. Naming a blob address as "the screen" sent the model looking
 * for a route that does not exist (seen on local with "Fix with AI"). Only a path that starts with
 * a slash and is not an address counts; a WebContainer preview still reports `/contacts`.
 */
export function screenRoute(pathname: string | undefined): string | null {
  const path = (pathname ?? '').trim();
  if (!path.startsWith('/') || path === '/' || path.includes('://')) return null;
  return path;
}

export function buildPreviewCrashFixPrompt(
  crash: Pick<WorkbenchPreviewCrash, 'kind' | 'message' | 'stack' | 'file' | 'pathname'>
): string {
  const route = screenRoute(crash.pathname);
  const where = route ? ` on the \`${route}\` screen` : '';
  const summary =
    crash.kind === 'compile'
      ? `The prototype failed to compile${where}, so the preview shows nothing.`
      : `The prototype crashed while rendering${where}, so the preview is blank.`;

  let message = clean(crash.message) || 'Unknown error';
  if (message.length > MAX_MESSAGE_CHARS) message = `${message.slice(0, MAX_MESSAGE_CHARS)}…`;

  const lines = [summary, '', 'Error:', '```', message, '```'];
  if (crash.file) lines.push(`File: \`${workspacePath(crash.file)}\``);

  const stack = crash.kind === 'runtime' ? stackHead(crash.stack, message) : '';
  if (stack) lines.push('', 'Stack:', '```', stack, '```');

  lines.push(
    '',
    'Fix the code so this screen renders again. Keep the rest of the prototype unchanged.'
  );
  return lines.join('\n');
}

/**
 * Chef's repair payload (`RepairSchema` in chef/app/lib/.server/prototyper/taskflowSpec.ts, which
 * is strict: no other keys). "Fix with AI" sends it on a build of the crashed version itself, so
 * the model gets the error and the file straight away instead of a chat message for Nexus to plan,
 * which asked the founder which screen was blank (prototyper test finding 5).
 */
export interface PreviewCrashRepair {
  build_errors: Array<{ file: string | null; line: number | null; message: string }>;
  runtime_errors: Array<{ message: string; route: string | null; stack: string | null }>;
  changed_files: string[];
  note: string;
}

/** `src/App.tsx:12:3: Unexpected }` — esbuild's compile error line. */
const COMPILE_LINE = /^([^\s:]+\.[a-z]+):(\d+):\d+:\s*(.+)$/i;
const MAX_REPAIR_ERRORS = 5;

/**
 * @param changedFiles workspace paths the founder edited by hand and has not built; the build
 *   request carries their contents (`workbenchManualFiles`), this only names them for the model.
 */
export function previewCrashRepair(
  crash: Pick<WorkbenchPreviewCrash, 'kind' | 'message' | 'stack' | 'file' | 'pathname'>,
  changedFiles: readonly string[] = []
): PreviewCrashRepair {
  const message = clean(crash.message) || 'Unknown error';
  const file = crash.file && !crash.file.startsWith('blob:') ? workspacePath(crash.file) : null;
  const route = screenRoute(crash.pathname);

  if (crash.kind === 'compile') {
    const build_errors = message
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .slice(0, MAX_REPAIR_ERRORS)
      .map((line) => {
        const match = COMPILE_LINE.exec(line);
        return match
          ? { file: match[1], line: Number(match[2]), message: match[3].slice(0, MAX_MESSAGE_CHARS) }
          : { file, line: null, message: line.slice(0, MAX_MESSAGE_CHARS) };
      });
    return {
      build_errors,
      runtime_errors: [],
      changed_files: [...changedFiles],
      note: 'The prototype does not compile, so the preview shows nothing. Fix these errors and keep everything else as it is.',
    };
  }

  const stack = stackHead(crash.stack, message);
  return {
    build_errors: [],
    runtime_errors: [
      {
        message: file ? `${message} (in ${file})` : message,
        route,
        stack: stack || null,
      },
    ],
    changed_files: [...changedFiles],
    note: `The prototype crashed while rendering${route ? ` the ${route} screen` : ''}${
      file ? `; the error is in ${file}` : ''
    }. Fix the cause of the crash and keep everything else as it is.`,
  };
}
